/**
 * useImportRun — react-query-backed polling hook for a single
 * `shop_history_import_runs` row.
 *
 * Why polling (not realtime):
 *   The user spec for Slice 2.5 explicitly de-scoped realtime
 *   subscriptions. Polling gives a debuggable, low-coupling
 *   progress surface that we can swap to realtime in a later
 *   slice. The wizard's expected import durations (seconds to a
 *   couple of minutes for a typical first-shop CSV) fit a 2-second
 *   poll cadence comfortably.
 *
 * Auto-stop:
 *   The hook stops polling as soon as the run reaches a terminal
 *   state ('analyzed' or 'failed'). The progress UI keeps showing
 *   the last result until the user navigates away.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ImportRunStatus =
  | 'staging'
  | 'uploaded'
  | 'promoting'
  | 'promoted'
  | 'analyzing'
  | 'analyzed'
  | 'failed'
  | 'discarded';

/** A row the parser could not read (Core-100 R4, CORE-V): its number, field and reason — never a value. */
export interface ImportUnresolvedRow {
  row: number | null;
  field: string | null;
  reason: string;
}

export interface ImportRunRow {
  id: string;
  shop_id: string;
  source_format: string;
  time_zone: string;
  source_filename: string | null;
  status: ImportRunStatus;
  chunk_total: number;
  chunk_promoted: number;
  rows_total: number;
  rows_promoted: number;
  rows_invalid: number;
  rows_skipped_duplicate: number;
  /** Rows the parser could not read; promotion waits until an owner/manager acknowledges them. */
  rows_unresolved?: number;
  unresolved_rows?: ImportUnresolvedRow[];
  unresolved_acknowledged_at?: string | null;
  error_message: string | null;
  last_progress_at: string;
  created_at: string;
}

const TERMINAL: ReadonlySet<ImportRunStatus> = new Set(['analyzed', 'failed', 'discarded']);

const SHOP_HISTORY_IMPORT_RUNS_TABLE =
  'shop_history_import_runs' as unknown as Parameters<typeof supabase.from>[0];

export interface UseImportRunOptions {
  shopId: string | null;
  importRunId: string | null;
  /** Polling cadence in ms. Defaults to 2000. */
  intervalMs?: number;
  /** When false the hook stops fetching entirely. */
  enabled?: boolean;
}

export interface UseImportRunResult {
  run: ImportRunRow | null;
  isLoading: boolean;
  isError: boolean;
  isTerminal: boolean;
  refetch: () => Promise<void>;
}

export function useImportRun({
  shopId,
  importRunId,
  intervalMs = 2000,
  enabled = true,
}: UseImportRunOptions): UseImportRunResult {
  const query = useQuery({
    queryKey: ['shop_history_import_runs', shopId, importRunId],
    enabled: enabled && Boolean(shopId) && Boolean(importRunId),
    refetchInterval: (q) => {
      const data = (q.state.data ?? null) as ImportRunRow | null;
      if (data && TERMINAL.has(data.status)) return false;
      return intervalMs;
    },
    queryFn: async (): Promise<ImportRunRow | null> => {
      if (!shopId || !importRunId) return null;
      // The shop_history_* tables are added by Phase 2.2 migrations
      // that have NOT been applied yet at the time this code lands,
      // so they are absent from the generated Database type. Cast
      // via `unknown` so we don't tie this hook to a type-regeneration
      // gate. The runtime shape is enforced by the SELECT column list.
      // deno-lint-ignore no-explicit-any
      const client = supabase as any;
      const { data, error } = await client
        .from(SHOP_HISTORY_IMPORT_RUNS_TABLE)
        .select(
          'id, shop_id, source_format, time_zone, source_filename, status, chunk_total, chunk_promoted, rows_total, rows_promoted, rows_invalid, rows_skipped_duplicate, rows_unresolved, unresolved_rows, unresolved_acknowledged_at, error_message, last_progress_at, created_at',
        )
        .eq('id', importRunId)
        .eq('shop_id', shopId)
        .maybeSingle();
      if (error) throw error;
      return ((data as unknown) as ImportRunRow | null) ?? null;
    },
  });

  const run = query.data ?? null;
  return {
    run,
    isLoading: query.isLoading,
    isError: query.isError,
    isTerminal: Boolean(run && TERMINAL.has(run.status)),
    refetch: async () => {
      await query.refetch();
    },
  };
}

export const _internal = { TERMINAL };
